import { useQuery } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";
import { useState } from "react";

import { link, type BrokerClient, type PlatformClient } from "@/api/client";
import { Chip, Value } from "@/components/Chip";

type Filter = "all" | "online" | "offline" | "devices" | "platform";

interface Row {
  id: string;
  src: "BROKER" | "WORKBENCH" | "PLATFORM";
  kind: string;
  online: boolean;
  state: string | null;
  uptime: string;
  ip: string;
  proto: string;
  keepalive: number | null;
  io: string;
  subs: string;
}

const STATE_TONE: Record<string, "ok" | "bad" | "warn" | "mute"> = {
  online: "ok",
  killed: "bad",
  error: "bad",
  connecting: "warn",
  stopped: "mute",
};

export function ConnectionsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");

  const stats = useQuery({ queryKey: ["link", "stats"], queryFn: link.stats, refetchInterval: POLL.STATE });
  const clients = useQuery({ queryKey: ["link", "clients"], queryFn: link.clients, refetchInterval: POLL.STATE });

  const m = stats.data?.metrics;
  const b = stats.data?.broker;

  const rows: Row[] = [
    ...(clients.data?.broker ?? []).map((c: BrokerClient) => ({
      id:
        c.clientid && c.username && c.clientid !== c.username
          ? `${c.clientid} (${c.username})`
          : (c.clientid ?? c.username ?? "?"),
      src: "BROKER" as const,
      kind: "device",
      online: !!c.connected,
      state: null,
      uptime: c.connected_at ?? "—",
      ip: c.ip_address ?? "—",
      proto: `MQTT ${c.proto_ver ?? "?"}`,
      keepalive: null,
      io: `${c.recv_msg ?? 0} / ${c.send_msg ?? 0}`,
      subs: `${c.subscriptions_cnt ?? 0} subs`,
    })),
    ...(clients.data?.platform ?? []).map((c: PlatformClient) => ({
      id: c.identity,
      src: (c.source === "workbench" ? "WORKBENCH" : "PLATFORM") as "WORKBENCH" | "PLATFORM",
      kind: c.kind,
      online: c.connected,
      state: c.state,
      uptime: c.uptime_s != null ? `${c.uptime_s}s` : "—",
      ip: c.ip_address,
      proto: `MQTT ${c.proto_ver}`,
      keepalive: c.keepalive,
      io: `${c.recv_msg ?? "—"} / ${c.send_msg ?? "—"}`,
      subs: c.subscriptions.join(", ") || "—",
    })),
  ];

  const shown = rows.filter((r) => {
    if (filter === "online" && !r.online) return false;
    if (filter === "offline" && r.online) return false;
    if (filter === "devices" && !["telematics", "device"].includes(r.kind)) return false;
    if (filter === "platform" && !(r.src === "PLATFORM" || ["server", "service"].includes(r.kind)))
      return false;
    if (q && !`${r.id} ${r.ip} ${r.subs} ${r.src}`.toLowerCase().includes(q.toLowerCase()))
      return false;
    return true;
  });

  const FILTERS: { k: Filter; label: string }[] = [
    { k: "all", label: "All" },
    { k: "online", label: "Online" },
    { k: "offline", label: "Offline" },
    { k: "devices", label: "Devices only" },
    { k: "platform", label: "Platform" },
  ];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Link · Connections" title="Devices on the broker"
        right={<span className="livepill">
          {b?.connected ? (
            <>
              <span className="gdot" /> BROKER LIVE · {b.kind.toUpperCase()}
            </>
          ) : (
            <>
              <span className="dot" style={{ background: "var(--bad)" }} /> BROKER DOWN
            </>
          )}
        </span>} />

      <div className="livegrid" style={{ marginBottom: 16 }}>
        <div>
          <div className="k">Connected clients</div>
          <div className="v num">
            <Value v={m?.connected_clients ?? null} />
          </div>
          <div className="s">mTLS sessions</div>
        </div>
        <div>
          <div className="k">Telemetry in · rate</div>
          <div className="v num">
            <Value v={m?.msgs_per_min_in != null ? m.msgs_per_min_in.toFixed(1) : null} />
          </div>
          <div className="s">PUBLISH msg/min</div>
        </div>
        <div>
          <div className="k">Telemetry in · total</div>
          <div className="v num">
            <Value v={m?.publish_in ?? null} />
          </div>
          <div className="s">application PUBLISHes</div>
        </div>
        <div>
          <div className="k">Auth failures</div>
          <div className="v num">
            <Value v={m?.auth_failures ?? null} />
          </div>
          <div className="s">EMQX only</div>
        </div>
        <div>
          <div className="k">ACL denials</div>
          <div className="v num">
            <Value v={m?.acl_denials ?? null} />
          </div>
          <div className="s">EMQX only</div>
        </div>
      </div>

      <div className="toolrow">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            className={`fchip${filter === f.k ? " on" : ""}`}
            onClick={() => setFilter(f.k)}
          >
            {f.label}
          </button>
        ))}
        <input
          className="search"
          style={{ marginLeft: "auto" }}
          placeholder="identity · IP · topic…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Identity (cert CN)</th>
              <th>Source</th>
              <th>State</th>
              <th>Uptime</th>
              <th>IP</th>
              <th>Protocol</th>
              <th>In / Out</th>
              <th>Subscriptions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: "18px 16px", color: "var(--t-dim)" }}>
                  No connections match this filter.
                </td>
              </tr>
            ) : (
              shown.map((r) => (
                <tr key={`${r.src}:${r.id}`}>
                  <td className="dev mono">{r.id}</td>
                  <td>
                    <Chip tone={r.src === "BROKER" ? "info" : r.src === "WORKBENCH" ? "warn" : "mute"}>
                      {r.src}
                    </Chip>{" "}
                    <span style={{ fontSize: 10, color: "var(--t-dim)" }}>{r.kind}</span>
                  </td>
                  <td>
                    {r.state ? (
                      <Chip tone={STATE_TONE[r.state] ?? "mute"}>{r.state.toUpperCase()}</Chip>
                    ) : (
                      <Chip tone={r.online ? "ok" : "mute"}>{r.online ? "ONLINE" : "OFFLINE"}</Chip>
                    )}
                  </td>
                  <td>
                    {r.uptime}
                    {r.keepalive != null && (
                      <span style={{ fontSize: 10, color: "var(--t-dim)" }}> ka {r.keepalive}s</span>
                    )}
                  </td>
                  <td className="mono">{r.ip}</td>
                  <td>{r.proto}</td>
                  <td className="num">{r.io}</td>
                  <td className="mono" style={{ fontSize: 10.5 }}>
                    {r.subs}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10.5, color: "var(--t-dim)", padding: "10px 4px 0" }}>
        {clients.data?.broker_supported ? (
          <>Identity = client-certificate CN (OLK-01). BROKER rows come from EMQX; WORKBENCH/PLATFORM rows are sessions Orbit owns.</>
        ) : (
          <>
            <b className="muted">Broker rows unavailable:</b>{" "}
            {clients.data?.broker_reason}
            <br />
            Rows above are sessions <b>Orbit itself owns</b> - real mTLS clients, from our own
            registry (instant, no broker round-trip).
          </>
        )}
      </div>
    </div>
  );
}
